import type {
	AppConfigMasteryLevelsType,
	CustomConfigType,
	LogoConfigSchemaType,
} from "@memorang/constants/config/schema";
import { getFallbackProficiencyLevelLabel } from "@memorang/helpers/language-learning";
import {
	SKILL_MASTERY_WINDOW,
	resolveMasteryForTagId,
} from "@memorang/helpers/language-learning-mastery";
import {
	compareTagSortOrder,
	isProficiencyTag,
	isSkillTag,
} from "@memorang/helpers/tag";
import type {
	Distribution,
	MixedItemTag,
	PracticeTestExam,
	RecommendationContext,
} from "@memorang/types";
import type { ExamInfoStat } from "@memorang/ui/components/HomeApplet/ExamInfoCard";
import { getSkillIcon } from "@memorang/ui/constants";
import type { AppTheme } from "@memorang/ui/useAppTheme";
import type { TFunction } from "i18next";
import {
	buildSkillCta,
	getSkillCtaKind,
	hasSkillLevelEvidence,
} from "../../helpers/skill-cta";

type HomeHeaderIconMode = NonNullable<
	NonNullable<CustomConfigType["homeView"]>["headerIconMode"]
>;
type CompanyIcon = LogoConfigSchemaType["company"];

const getStudyItemsAttempted = (distribution: Distribution) =>
	Object.values(distribution).reduce((a, b) => a + b, 0) -
	(distribution.none ?? 0);

export const getCompletionProgress = (distribution: Distribution) => {
	const total = Object.values(distribution).reduce((a, b) => a + b, 0);
	const percent =
		total > 0
			? Math.round((getStudyItemsAttempted(distribution) / total) * 100)
			: 0;

	return [
		{ x: "completed", y: percent },
		{ x: "remaining", y: 100 - percent },
	];
};

const MAX_STAT_TILES = 4;

const STAT_TILES_PLACEHOLDER_COUNT = 2;

export const getLanguageLearningStatsPlaceholder = (): ExamInfoStat[] =>
	Array.from({ length: STAT_TILES_PLACEHOLDER_COUNT }, (_, index) => ({
		icon: "school-outline",
		label: `skill-placeholder-${index}`,
		value: "",
	}));

export const getLanguageLearningStats = ({
	tags,
	recommendationContext,
	startableTests,
	hasPracticeForSkill,
	t,
}: {
	tags: MixedItemTag[];
	recommendationContext: RecommendationContext | undefined;
	startableTests: PracticeTestExam[] | undefined;
	hasPracticeForSkill: (skillId: number) => boolean;
	t: TFunction;
}): ExamInfoStat[] | undefined => {
	const proficiencyTags = tags.filter(isProficiencyTag);
	const skillTags = tags
		.filter(isSkillTag)
		.sort(compareTagSortOrder)
		.slice(0, MAX_STAT_TILES);
	const fallbackLevelLabel = getFallbackProficiencyLevelLabel(proficiencyTags);

	const stats = skillTags.map((skill): ExamInfoStat => {
		const { estimatedLevel, displayLevel } = resolveMasteryForTagId(
			recommendationContext,
			skill.id,
			SKILL_MASTERY_WINDOW,
			fallbackLevelLabel,
		);
		const showLevel =
			Boolean(estimatedLevel) ||
			hasSkillLevelEvidence({ skill, exams: startableTests });
		const cta = showLevel
			? undefined
			: buildSkillCta(
					getSkillCtaKind({
						skill,
						startableTests,
						hasPracticeRecommendation: hasPracticeForSkill(skill.id),
					}),
					t,
				);
		return {
			skillId: String(skill.id),
			icon: getSkillIcon(skill.title),
			value: displayLevel ?? "",
			label: skill.title,
			cta,
			tileState: cta ? "cta" : "known",
			tooltip:
				cta || !displayLevel
					? undefined
					: t("dashboard.stats.skillLevelTooltip", {
							skill: skill.title,
							level: displayLevel,
						}),
		};
	});

	if (stats.length === 0) {
		return undefined;
	}

	return stats;
};

export const getMasteryProgressData = (
	dist: Distribution,
	sortedMasteryLevels: AppConfigMasteryLevelsType,
) => {
	const distMap = Object.keys(dist).reduce<Record<string, number>>(
		(acc, curr) => {
			acc[curr] = dist[curr as keyof Distribution];
			return acc;
		},
		{},
	);

	return [
		...sortedMasteryLevels.map((level) => ({
			x: level.id,
			y: distMap[level.id] || 0,
		})),
		{ x: "none", y: distMap.none || 0 },
	];
};

export const getProgressColorScale = (
	theme: AppTheme,
	sortedMasteryLevels: AppConfigMasteryLevelsType,
): string[] => {
	const { none: empty } = theme.palette.progress;

	return [
		...sortedMasteryLevels.map((level) =>
			theme.dark ? level.darkColor : level.lightColor,
		),
		empty,
	];
};

export const buildHomeHeaderIcon = ({
	headerIconMode,
	examIcon,
	companyIcon,
	isDark,
}: {
	headerIconMode?: HomeHeaderIconMode;
	examIcon?: string;
	companyIcon?: CompanyIcon;
	isDark: boolean;
}): string | undefined => {
	if (headerIconMode === "exam") {
		return examIcon;
	}

	if (headerIconMode === "company") {
		return isDark ? companyIcon?.dark?.uri : companyIcon?.light?.uri;
	}

	return undefined;
};
